import * as THREE from 'three';
import { Viewer } from '../Viewer';
import { Vector3 } from 'three';
import { ViewerScene } from '../ViewerScene';
import { HsbEntity, SceneLayerName } from '../../hsbcad-model';
import { EventEmitter } from 'events';
import { ViewerLayers } from '../Enums/ViewerLayers';
import { LoadingSteps } from '../LoadingProgress';
import { HSBControlsEventTypes } from '../Camera/HSBControls';

/**
 * Class that loads scenes and prepares them for the viewer.
 */
export class SceneManager {
    _scenes: Array<ViewerScene> = new Array<ViewerScene>();
    /**
     * THe currently active scene
     */
    private _currentScene: ViewerScene | null = null;
    /**
     * THe currently active scene
     */
    public get currentScene(): ViewerScene | null {
        return this._currentScene;
    }

    private _listeners: Map<HSBControlsEventTypes, (() => void) | null> = new Map([
        ['start', null],
        ['change', null],
        ['end', null],
        ['held', null],
    ]);
    /**
     * True if the camera is being controlled for >250ms
     */
    private _cameraThreshold: boolean = false;
    /**
     * True if the camera is being controlled for >250ms
     */
    public get CameraThreshold(): boolean {
        return this._cameraThreshold;
    }

    private _cameraTimeout: any;
    /**
     * THe currently active scene
     */
    public set currentScene(v: ViewerScene | null) {
        let self = this;
        if (this._currentScene) {
            // get rid of current listeners
            this._listeners.forEach((listener, name) => {
                if (listener) self._currentScene?.CameraControls?.removeEventListener(name, listener);
            });
        }
        this._currentScene = v;
        if (this._currentScene) {
            // Explicit rendering
            let changeFoo = () => this._viewer.requestRenderFrame('camera');
            this._listeners.set('change', changeFoo);
            this._currentScene.CameraControls?.addEventListener('change', changeFoo);

            // Lock tools if camera busy for 0.5s
            let heldFoo = () => (this._cameraThreshold = true);
            this._currentScene.CameraControls?.addEventListener('held', heldFoo);
            let endFoo = () => (this._cameraThreshold = false);
            this._currentScene.CameraControls?.addEventListener('end', endFoo);
        }
    }
    private _light: THREE.AmbientLight | null = null;
    public get light(): THREE.AmbientLight | null {
        return this._light;
    }
    _sceneEvent: EventEmitter = new EventEmitter();

    _loadedGeometry: any;

    constructor(private _viewer: Viewer) {}

    /**
     * Adds either an Object3D or an array of Object3D to the currently active scene.
     * @param models Either 1 Object3D or an array of Object3D's to add to the active scene
     * @param asGroup Whether or not to group the model(s) in a group object
     * @returns The root at which the objects were addewd
     */
    public AddModelsToActiveScene(models: THREE.Object3D | Array<THREE.Object3D>, asGroup: boolean = false): THREE.Object3D | null {
        if (!this.currentScene) return null;
        return this.AddModelsToScene(models, this.currentScene, asGroup);
    }

    /**
     * Adds either an Object3D or an array of Object3D to the given scene.
     * @param models Either 1 Object3D or an array of Object3D's to add to the given scene
     * @param scene THe scene to which the model(s) should be added
     * @param asGroup Whether or not to group the model(s) in a group object
     * @returns THe root at which the object were added.
     */
    public AddModelsToScene(
        models: THREE.Object3D | Array<THREE.Object3D>,
        scene: ViewerScene,
        asGroup: boolean = false,
        addGround: boolean = false
    ): THREE.Object3D | null {
        let root: THREE.Group | THREE.Object3D;
        if (asGroup) {
            root = new THREE.Group();
            root.name = 'Subroot';
            scene.GetLayer(SceneLayerName.Entities).add(root);
        } else {
            root = scene.GetLayer(SceneLayerName.Entities);
        }
        var box: any;
        if (Array.isArray(models)) {
            box = new THREE.Box3();
            models.forEach((model) => {
                box.union(new THREE.Box3().setFromObject(model));
            });

            /**
             * Reposition all elements to have a certain local distance from the center of the model. THeir world position stays the same but like this we can properly transform the whole model
             */
            let center: THREE.Vector3 = new THREE.Vector3();
            box.getCenter(center);
            models.forEach((model) => {
                model.position.subVectors(model.position, center);
                root.add(model);
            });

            root.position.copy(center);
        } else if (models instanceof THREE.Object3D) {
            box = new THREE.Box3().setFromObject(models);
            root.add(models);
        } else {
            return null;
        }
        root.rotateX(-Math.PI / 2);

        if (addGround) {
            let size = new THREE.Vector3();
            box.getSize(size);
            let center = new THREE.Vector3();
            box.getCenter(center);
            let bottomPos = new Vector3(0, 0, -size.z / 2);

            let groundGeom = new THREE.PlaneGeometry(size.x * 1.5, size.y * 1.5);
            let groundMaterial = new THREE.MeshLambertMaterial({
                color: 0x707070,
                side: THREE.DoubleSide,
                opacity: 0.75,
                transparent: true,
            });
            let ground = new THREE.Mesh(groundGeom, groundMaterial);
            let edgeMat = new THREE.LineBasicMaterial({ color: 0x404040 });
            let edgeGeom = new THREE.EdgesGeometry(groundGeom);
            let groundEdge = new THREE.LineSegments(edgeGeom, edgeMat);
            ground.add(groundEdge);
            ground.layers.set(ViewerLayers.INFO);
            root.add(ground);
            ground.position.copy(bottomPos);
        }

        return root;
    }

    /**
     * Returns the scene for the given name. Null if no scene with the given name was found.
     * @param scene
     */
    public GetScene(scene: string): ViewerScene | undefined {
        return this._scenes.find((x) => x.name === scene);
    }
    /**
     * Creates a default scene.
     * @param camera The camera to use in this scene
     * @param controls The controls to sue for this camera
     */
    public CreateDefaultScene(activate: boolean = true): ViewerScene {
        let scene = this.CreateEmptyScene('Default Scene', false);
        scene.currentCamera?.position.set(0, 0, -100);
        scene.currentCamera?.lookAt(scene.position);
        scene.currentCamera?.updateMatrix();
        scene.CameraControls?.update();
        scene.background = new THREE.Color(0xe0dfda);
        // scene.CameraControls.autoRotate = true;
        if (activate) {
            this.currentScene = scene;
            this._sceneEvent.emit('change', this.currentScene);
        }
        return scene;
    }

    /**
     * Creates an empty scene. The scene will contain cameras, controls, a grid and lighting.
     * @param name The name for the new scene
     * @param activate Whether to set the new scene as the active one.
     */
    public CreateEmptyScene(name: string, activate: boolean = true): ViewerScene {
        let newScene = new ViewerScene(this._viewer._width, this._viewer._height, this._viewer._renderer, {picker: this._viewer._picker});
        newScene.currentCamera?.position.copy(new THREE.Vector3(0, 0, 10));
        newScene.name = name;
        let camera = newScene.currentCamera;
        camera?.lookAt(newScene.position);
        camera?.updateMatrix();
        newScene.CameraControls?.update();
        // Lighting
        newScene.light = new THREE.AmbientLight(0xffffe0, 0.95);
        newScene.add(newScene.light);

        if (activate) {
            let oldScene = this.currentScene;
            this.currentScene = newScene;
            this.currentScene.CameraControls?.addEventListener;
            this._sceneEvent.emit('change', this.currentScene);
            if (oldScene) this.DisposeScene(oldScene);
        }

        this._scenes.push(newScene);
        return newScene;
    }

    /**
     * Disposes every object, geometry and material in a scene and sets the scene to null.
     * @param scene
     */
    public DisposeScene(scene: ViewerScene) {
        let self = this;
        if (!scene) {
            throw new Error('Viewer::DisposeScene(): Passed a null reference! Ensure a reference to a scene is given!');
        }

        scene.onDispose();
        if (scene === this.currentScene) {
            this.currentScene = null;
            this._sceneEvent.emit('change', this.currentScene);
        }
        try {
            let children = scene.children;
            while (children.length > 0) {
                let child = children[0];
                // eslint-disable-next-line no-loop-func
                child.traverse((object) => {
                    // Make sure entity doesnt ref object anymore
                    if (object instanceof THREE.InstancedMesh) {
                        for (let index = 0; index < object.userData.handle.length; index++) {
                            const handle = object.userData.handle?.[index];
                            if (handle) {
                                let entity = this._viewer.project?.EntityTable[object.userData.modelId]?.[handle];
                                if (entity) entity.object = null;
                            }
                        }
                    } else {
                        const handle = object.userData?.handle;
                        if (handle) {
                            let entity = this._viewer.project?.EntityTable[object.userData.modelId]?.[handle];
                            if (entity) entity.object = null;
                        }
                    }

                    scene.remove(child);
                    self.disposeObject(object);
                });

                if (child.type !== 'Scene') {
                    scene.remove(child);
                }
            }
        } catch (error) {
            console.error('SceneManager::DisposeScene(): Error:' + error);
        }
        const index = this._scenes.indexOf(scene, 0);
        if (index > -1) {
            this._scenes.splice(index, 1);
        }
        scene.currentCamera = null;
        scene.WireFrame = null;
    }

    /**
     * Removes an object or array of objects from the given scene. BE careful not to dispose objects that belong to multiple scenes!
     * @param scene the scene from which to remove the objects.
     * @param objects The objects to remove.
     */
    public RemoveFromScene(scene: ViewerScene, objects: THREE.Object3D | THREE.Object3D[]) {
        if (!objects || !scene) return;
        if (Array.isArray(objects)) {
            objects.forEach((object) => {
                scene.remove(object);
                this.disposeObject(object);
            });
        } else {
            scene.remove(objects);
            this.disposeObject(objects);
        }
    }

    /**
     * Removes an object or array of objects from the currentScene
     * @param objects
     */
    public RemoveFromActiveScene(objects: THREE.Object3D | THREE.Object3D[]) {
        if (!objects || !this.currentScene) return;

        if (Array.isArray(objects)) {
            objects.forEach((object) => {
                this.currentScene?.remove(object);
                this.disposeObject(object);
            });
        } else {
            this.currentScene.remove(objects);
            this.disposeObject(objects);
        }
    }

    /**
     * Properly disposes an object, including it's geometry and material.
     * @param object
     */
    private disposeObject(object: any) {
        if (!object) return;
        if (object.geometry) {
            object.geometry.dispose();
        }
        if (object.material) Array.isArray(object.material) ? object.material.forEach((mat: any) => mat.dispose()) : object.material.dispose();
        object = undefined;
    }
}

export {EventEmitter}